<template>
  <div class="flex flex-col">
    <div class="bg-surface-container-high rounded-extra-large">
      <div class="flex flex-col gap-10 p-4 lg:flex-row lg:p-6">
        <div class="-ml-2 flex flex-row items-center gap-4">
          <BackButton backup-route-name="impulse-library" />
          <img
            v-if="lesson.lessonImpulse"
            :src="lesson.lessonImpulse.image"
            :alt="lesson.lessonImpulse.title"
            class="rounded-small aspect-4/3 h-14 object-cover"
          />
          <div class="flex flex-col gap-1">
            <div
              class="rounded-small group relative -mx-1 overflow-hidden px-1"
              @click="beginRename"
            >
              <h1 class="text-title-large text-on-surface line-clamp-1 cursor-pointer">
                {{ lesson.title }}
              </h1>
              <BaseStateLayer type="filled" />
            </div>
            <div class="flex flex-row gap-1">
              <div
                class="bg-surface text-on-surface rounded-small text-label-medium flex flex-row items-center gap-1 px-2 py-1"
              >
                <span>{{ $t('lumia.lesson.endedAt') }} </span>
                <TimeAgo :date="lesson.endedAt!" />
              </div>
              <div
                v-for="team in lesson.teams"
                :key="team.id"
                class="bg-surface text-on-surface rounded-small text-label-medium flex flex-row items-center gap-1.5 py-1 pr-2 pl-1.5"
              >
                <BaseTeamAvatar :name="team.name" size="18" />
                <span>
                  {{ team.name }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <BaseButtonGroup v-if="actions.length" size="M" class="shrink-0 lg:ml-auto">
          <BaseComputedButtonGroup
            class="flex w-full flex-row lg:justify-end"
            size="M"
            menu-button-color="outlined"
            :buttons="actions"
          >
          </BaseComputedButtonGroup>
        </BaseButtonGroup>
      </div>
      <BaseDialog
        :is-open="isRenameDialogOpen"
        type="basic"
        :title="$t('lumia.lesson.renameDialog.title')"
        class="w-full max-w-md"
        @close="cancelRename"
      >
        <BaseInputField
          v-model="titleDraft"
          type="text"
          required
          autofocus
          class="pb-2"
          :label="$t('common.title')"
          :error="titleError"
          @keydown.enter="saveTitle"
        />
        <template #dismiss>
          <BaseTextButton color="text" size="M" @click="cancelRename">
            {{ $t('common.cancel') }}
          </BaseTextButton>
        </template>
        <template #confirm>
          <BaseTextButton color="filled" size="M" @click="saveTitle">
            {{ $t('common.save') }}
          </BaseTextButton>
        </template>
      </BaseDialog>
    </div>

    <LessonFeedbackLinkDialog
      :is-open="isFeedbackLinkDialogOpen"
      :teams="lesson.teams"
      @close="closeFeedbackLinkDialog"
    />

    <BaseDialog
      :is-open="isDeleteDialogOpen"
      type="basic"
      :title="$t('lumia.lesson.deleteDialog.title')"
      :description="$t('lumia.lesson.deleteDialog.description')"
      @close="closeDeleteDialog"
    >
      <template #dismiss>
        <BaseTextButton color="text" size="M" @click="closeDeleteDialog">
          {{ $t('common.cancel') }}
        </BaseTextButton>
      </template>
      <template #confirm>
        <BaseTextButton color="text" size="M" @click="confirmDelete">
          {{ $t('lumia.lesson.deleteDialog.action') }}
        </BaseTextButton>
      </template>
    </BaseDialog>
  </div>
</template>

<script lang="ts" setup>
  import { computed, ref, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useRouter } from 'vue-router'
  import BackButton from '@/core/components/BackButton.vue'
  import BaseButtonGroup from '@/core/components/base/button/BaseButtonGroup.vue'
  import BaseComputedButtonGroup from '@/core/components/base/button/BaseComputedButtonGroup.vue'
  import type { FlexibelButtonConfig } from '@/core/components/base/button/button-props'
  import BaseTextButton from '@/core/components/base/button/text-button/BaseTextButton.vue'
  import BaseTeamAvatar from '@/core/components/base/avatar/BaseTeamAvatar.vue'
  import BaseDialog from '@/core/components/base/dialog/BaseDialog.vue'
  import { useDialog } from '@/core/components/base/dialog/use-dialog'
  import BaseInputField from '@/core/components/base/form-fields/BaseInputField.vue'
  import BaseStateLayer from '@/core/components/base/state-layer/BaseStateLayer.vue'
  import { useLesson } from '../composables/use-lesson.ts'
  import type { LessonDetail } from '../models/lesson.ts'
  import TimeAgo from '@/core/components/base/time-ago/TimeAgo.vue'
  import LessonFeedbackLinkDialog from './LessonFeedbackLinkDialog.vue'

  interface Props {
    lesson: LessonDetail
  }

  const props = defineProps<Props>()

  const emit = defineEmits<{
    rename: [title: string]
  }>()

  const { t } = useI18n()
  const router = useRouter()
  const { deleteLesson } = useLesson()

  const {
    isOpen: isDeleteDialogOpen,
    openDialog: openDeleteDialog,
    closeDialog: closeDeleteDialog,
  } = useDialog()
  const {
    isOpen: isFeedbackLinkDialogOpen,
    openDialog: openFeedbackLinkDialog,
    closeDialog: closeFeedbackLinkDialog,
  } = useDialog()

  const openLessonImpulse = () => {
    const impulseId = props.lesson.lessonImpulse?.id
    if (!impulseId) return
    router.push({
      name: 'impulse-library-detail',
      params: { id: impulseId },
    })
  }

  const openTeacherAssessment = () => {
    router.push({
      name: 'lesson-teacher-assessment',
      params: { lessonId: props.lesson.id },
    })
  }

  const actions = computed<FlexibelButtonConfig[]>(() => [
    {
      id: 'view-impulse',
      condition: props.lesson.lessonImpulse != null,
      label: t('lumia.lesson.viewImpulse'),
      icon: 'menu_book',
      color: 'outlined',
      callback: openLessonImpulse,
      flexibelConfigurations: [
        { type: 'text', order: 1 },
        { type: 'icon', order: 2 },
        { type: 'menu', order: 3 },
      ],
    },
    {
      id: 'view-teacher-assessment',
      condition: props.lesson.feedback?.teacherAssessment != null,
      label: t('lumia.lesson.viewTeacherAssessment'),
      icon: 'rate_review',
      color: 'outlined',
      callback: openTeacherAssessment,
      flexibelConfigurations: [
        { type: 'text', order: 1 },
        { type: 'icon', order: 2 },
        { type: 'menu', order: 3 },
      ],
    },
    {
      id: 'feedback-link',
      condition: props.lesson.feedback != null && props.lesson.teams.length > 0,
      label: t('lumia.lesson.feedback.showLink'),
      icon: 'share',
      color: 'outlined',
      callback: openFeedbackLinkDialog,
      flexibelConfigurations: [{ type: 'menu', order: 2 }],
    },
    {
      id: 'delete',
      label: t('common.delete'),
      icon: 'delete',
      color: 'outlined',
      shape: 'square',
      destructive: true,
      callback: openDeleteDialog,
      flexibelConfigurations: [{ type: 'menu', order: 1 }],
    },
  ])

  const confirmDelete = async () => {
    closeDeleteDialog()
    await deleteLesson()
  }

  const titleDraft = ref<string>()
  const titleError = ref<string>()
  const {
    isOpen: isRenameDialogOpen,
    openDialog: openRenameDialog,
    closeDialog: closeRenameDialog,
  } = useDialog()

  watch(titleDraft, () => {
    titleError.value = undefined
  })

  const beginRename = () => {
    titleDraft.value = props.lesson.title
    titleError.value = undefined
    openRenameDialog()
  }

  const cancelRename = () => {
    titleError.value = undefined
    closeRenameDialog()
  }

  const saveTitle = () => {
    const nextTitle = titleDraft.value?.trim() ?? ''
    if (!nextTitle) {
      titleError.value = 'lumia.lesson.validation.titleRequired'
      return
    }
    titleError.value = undefined
    emit('rename', nextTitle)
    closeRenameDialog()
  }
</script>
